import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else {
          if (attempts < 5) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus('timeout');
          }
        }
      } catch {
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    poll();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div data-testid="payment-checking">
            <Loader2 className="w-16 h-16 text-[#CC5A3D] animate-spin mx-auto mb-6" />
            <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-2">Confirming Payment</h1>
            <p className="text-sm text-[#6B726A]">Please wait while we verify your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-confirmed">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-2">Booking Confirmed!</h1>
            <p className="text-sm text-[#6B726A] mb-6">Your payment was successful. A confirmation email will be sent shortly.</p>
            <p className="text-xs text-[#6B726A] mb-8">You'll receive pre-arrival instructions closer to your check-in date.</p>
            <Link to="/">
              <Button className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-8" data-testid="back-home-btn">Back to Home</Button>
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 bg-[#BC4749] rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-2">
              {status === 'expired' ? 'Session Expired' : status === 'timeout' ? 'Status Check Timeout' : 'Something Went Wrong'}
            </h1>
            <p className="text-sm text-[#6B726A] mb-6">
              {status === 'expired' ? 'Your payment session has expired. Please try again.' : 'We couldn\'t confirm your payment. If you were charged, please contact us.'}
            </p>
            <Link to="/caravans">
              <Button className="bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm px-8" data-testid="try-again-btn">Try Again</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
